// The integrated model as a LinkML schema (task 2026-10-01-linkml-master-schema): anyone in the
// project downloads the live model (GET .../dbdesign/schema.linkml.yaml: the design plus every
// shared entity with all its slots); a curator imports a schema, whose classes become shared
// entities (POST .../dbdesign/linkml {schema}). The "Find key links" proposals then suggest the
// columns that belong to them. A version's schema is downloaded in the Build panel.
// Backend: unio/routers/dbdesign_linkml.py, unio/services/linkml.py.
import { useState } from "react";
import { api } from "../../api";
import { Btn } from "../../components/Shared";

function readText(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error || new Error("The file could not be read."));
    reader.readAsText(file);
  });
}

const small = { padding: "6px 12px", fontSize: 13 };

export default function LinkmlPanel({ projectId, slug, canManage, busy, imported, onImport }) {
  const [file, setFile] = useState(null);
  const [error, setError] = useState(null);
  const [inputKey, setInputKey] = useState(0);

  const download = () => api.download(`/projects/${projectId}/dbdesign/schema.linkml.yaml`, `${slug || "model"}.linkml.yaml`)
    .catch(setError);
  const submit = async e => {
    e.preventDefault();
    setError(null);
    let text;
    try { text = await readText(file); } catch (err) { setError(err); return; }
    if (await onImport(text)) { setFile(null); setInputKey(k => k + 1); }
  };
  const renamed = Object.entries(imported?.renamed || {});

  return (
    <div style={{ marginTop: 18, paddingTop: 14, borderTop: "1px solid #e5e7eb" }}>
      <div style={{ fontSize: 13, color: "#374151", marginBottom: 10, maxWidth: 820 }}>
        The model as a <a href="https://linkml.io/" target="_blank" rel="noreferrer">LinkML</a> schema: a class per
        table and shared entity, a slot per column with its type, description and key links.
        {canManage && " Importing a schema creates a shared entity per class before any data arrives; "
          + "Find key links then proposes the columns that belong to them."}
      </div>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
        <Btn variant="secondary" style={small} onClick={download}>Download the model (LinkML)</Btn>
        {canManage && (
          <form onSubmit={submit} style={{ display: "inline-flex", gap: 8, alignItems: "center", flexWrap: "wrap",
                                           maxWidth: "100%", minWidth: 0 }}>
            <label style={{ fontSize: 13, display: "inline-flex", gap: 6, alignItems: "center", flexWrap: "wrap",
                            maxWidth: "100%", minWidth: 0 }}>
              LinkML schema file
              <input key={inputKey} type="file" accept=".yaml,.yml,application/yaml,text/yaml" disabled={busy}
                     onChange={ev => setFile(ev.target.files[0] || null)} style={{ fontSize: 13, maxWidth: "100%", minWidth: 0 }} />
            </label>
            <Btn type="submit" style={small} disabled={busy || !file}>Import schema</Btn>
          </form>
        )}
        {error && <span role="alert" style={{ color: "#b91c1c", fontSize: 12 }}>{error.message}</span>}
      </div>
      {imported && (imported.ignored.length > 0 || renamed.length > 0) && (
        <div style={{ fontSize: 12, color: "#4b5563", marginTop: 10 }}>
          {renamed.length > 0 && (
            <p style={{ margin: "0 0 6px" }}>
              Renamed to the database's names: {renamed.map(([from, to]) => `${from} → ${to}`).join(", ")}.
            </p>
          )}
          {imported.ignored.length > 0 && (
            <>
              <p style={{ margin: "0 0 4px" }}>The shared model has no place for these, so they were not kept:</p>
              <ul aria-label="Not kept from the schema" style={{ margin: 0, paddingLeft: 18 }}>
                {imported.ignored.map(x => <li key={x} style={{ fontFamily: "'JetBrains Mono', monospace" }}>{x}</li>)}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  );
}
